// Manage the home-page "Recent work" carousel.
//
// These are just photos in S3 under featured-items/ — no product record, no
// price. The grid is in carousel order, and dragging a tile (or nudging it
// with the arrows) rearranges the carousel itself.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, GripVertical, Trash2, Upload } from 'lucide-react';
import {
  adminDeleteFeatured,
  adminRefreshFeaturedManifest,
  adminSetFeaturedOrder,
  adminUploadFeatured,
  describeApiError,
  getFeaturedImages,
} from '../../services/api';
import type { FeaturedImage } from '../../types';
import { useConfirm } from '../../hooks/useConfirm';

// Photos go straight to S3 from the browser, so this is the only size limit
// in play — there's no request-payload ceiling to stay under any more.
const MAX_MB = 25;

const FeaturedManagement: React.FC = () => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragFrom = useRef<number | null>(null);
  const [images, setImages] = useState<FeaturedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deleting, setDeleting] = useState<string | undefined>();
  const [dragOver, setDragOver] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();
  const { confirm, confirmDialog } = useConfirm();

  const load = () => {
    setLoading(true);
    getFeaturedImages()
      .then(setImages)
      .catch(e => setError(describeApiError(e, 'Could not load featured images')))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onPick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(undefined);
    setUploading(true);
    try {
      // Upload sequentially so one rejection doesn't obscure which file failed.
      const added: FeaturedImage[] = [];
      for (const file of Array.from(files)) {
        if (file.size > MAX_MB * 1024 * 1024) {
          throw new Error(`${file.name} is over ${MAX_MB} MB.`);
        }
        added.push(await adminUploadFeatured(file));
      }
      setImages(prev => [...added, ...prev]);
      // The photos went straight to S3, so the API only learns
      // the gallery changed when we tell it.
      await adminRefreshFeaturedManifest();
    } catch (e: any) {
      setError(describeApiError(e, 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /**
   * Apply a new arrangement optimistically, then persist it.
   *
   * Dragging should feel immediate, so the grid moves first; if the save
   * fails the list is reloaded so what's on screen matches what the
   * carousel will actually show.
   */
  const applyOrder = async (next: FeaturedImage[]) => {
    const previous = images;
    setImages(next);
    setSavingOrder(true);
    setError(undefined);
    try {
      await adminSetFeaturedOrder(next.map(i => i.key));
    } catch (e: any) {
      setImages(previous);
      setError(describeApiError(e, 'Could not save the new order'));
    } finally {
      setSavingOrder(false);
    }
  };

  const moveTo = (from: number, to: number) => {
    if (from === to || to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    applyOrder(next);
  };

  const remove = async (image: FeaturedImage) => {
    const ok = await confirm({
      title: 'Remove this photo?',
      body: 'It comes off the home page carousel and is deleted for good.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(image.key);
    setError(undefined);
    try {
      await adminDeleteFeatured(image.key);
      setImages(prev => prev.filter(i => i.key !== image.key));
    } catch (e: any) {
      setError(describeApiError(e, 'Delete failed'));
    } finally {
      setDeleting(undefined);
    }
  };

  return (
    <div>
      {confirmDialog}
      <h1 className="text-2xl font-semibold mb-1">Featured items</h1>
      <p className="text-sm text-text-light mb-6">
        Photos of past work shown in the home page carousel, in the order they
        appear there. Drag a photo — or use the arrows — to rearrange. The
        section is hidden entirely while there are none.
      </p>

      {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

      <div className="mb-6 flex items-center flex-wrap gap-x-3 gap-y-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={e => onPick(e.target.files)}
          disabled={uploading}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          <Upload size={16} />
          {uploading ? 'Uploading…' : 'Add photos'}
        </button>
        <span className="text-xs text-text-light">
          JPG, PNG, GIF or WebP · up to {MAX_MB} MB each
        </span>
        {savingOrder && <span className="text-xs text-text-light">Saving order…</span>}
      </div>

      {loading ? (
        <div className="text-text-light">Loading…</div>
      ) : images.length === 0 ? (
        <div className="border border-dashed border-background-dark rounded p-10 text-center text-text-light">
          No featured photos yet. Add some and they'll appear on the home page.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={image.key}
              draggable
              onDragStart={() => {
                dragFrom.current = index;
              }}
              onDragEnd={() => {
                dragFrom.current = null;
                setDragOver(undefined);
              }}
              // Without preventDefault the browser refuses the drop outright.
              onDragOver={e => {
                e.preventDefault();
                setDragOver(index);
              }}
              onDragLeave={() => setDragOver(prev => (prev === index ? undefined : prev))}
              onDrop={e => {
                e.preventDefault();
                setDragOver(undefined);
                if (dragFrom.current !== null) moveTo(dragFrom.current, index);
                dragFrom.current = null;
              }}
              className={`relative group cursor-grab active:cursor-grabbing rounded ${
                dragOver === index ? 'ring-2 ring-primary ring-offset-2' : ''
              }`}
            >
              <div className="aspect-square rounded overflow-hidden border border-background-dark bg-background-dark">
                <img
                  src={image.url}
                  alt={image.filename}
                  draggable={false}
                  className="w-full h-full object-cover"
                />
              </div>

              <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90 text-text-light rounded-full pl-1.5 pr-2 py-0.5 text-xs shadow">
                <GripVertical size={12} />
                {index + 1}
              </span>

              <button
                type="button"
                onClick={() => remove(image)}
                disabled={deleting === image.key}
                aria-label={`Remove ${image.filename}`}
                className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50"
              >
                <Trash2 size={14} />
              </button>

              {/* Dragging doesn't work on touch screens, and isn't reachable
                  from a keyboard, so the same move is always available as a
                  pair of buttons. */}
              <div className="absolute bottom-2 inset-x-2 flex justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                <button
                  type="button"
                  onClick={() => moveTo(index, index - 1)}
                  disabled={index === 0 || savingOrder}
                  aria-label={`Move ${image.filename} earlier`}
                  className="bg-white/90 rounded-full p-1.5 shadow disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => moveTo(index, index + 1)}
                  disabled={index === images.length - 1 || savingOrder}
                  aria-label={`Move ${image.filename} later`}
                  className="bg-white/90 rounded-full p-1.5 shadow disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>

              <div className="text-xs text-text-light mt-1 truncate" title={image.filename}>
                {new Date(image.uploaded_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeaturedManagement;
